const MIN_CHUNK = 4 * 1024 * 1024;
const MAX_CHUNK = 64 * 1024 * 1024;
const TARGET_SECONDS = 15;
export const BLOB_WARN_BYTES = 1024 * 1024 * 1024;

class HttpError extends Error {
  constructor(status) { super(`HTTP ${status}`); this.status = status; }
}

async function fetchRange(url, start, end, signal, attempt = 0) {
  try {
    const res = await fetch(url, { headers: { Range: `bytes=${start}-${end}` }, signal });
    if (res.status !== 206 && res.status !== 200) throw new HttpError(res.status);
    const total = Number((res.headers.get("Content-Range") || "").split("/")[1]) || Number(res.headers.get("Content-Length"));
    return { data: new Uint8Array(await res.arrayBuffer()), total };
  } catch (err) {
    const permanent = err.name === "AbortError" || (err instanceof HttpError && err.status < 500);
    if (permanent || attempt >= 4) throw err;
    await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
    return fetchRange(url, start, end, signal, attempt + 1);
  }
}

export const canStreamToDisk = () => typeof window.showSaveFilePicker === "function";

async function openSink(filename) {
  if (canStreamToDisk()) {
    const handle = await window.showSaveFilePicker({ suggestedName: filename });
    const writable = await handle.createWritable();
    return { write: (d) => writable.write(d), close: () => writable.close(), abort: () => writable.abort() };
  }
  const parts = [];
  return {
    write: async (d) => { parts.push(d); },
    close: async () => {
      const href = URL.createObjectURL(new Blob(parts, { type: "video/mp4" }));
      const a = Object.assign(document.createElement("a"), { href, download: filename });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    },
    abort: async () => { parts.length = 0; },
  };
}

/** Downloads a large file in small Range requests (each well under the proxy's 60s limit). Returns false if cancelled. */
export async function chunkedDownload(url, filename, onProgress, signal) {
  let sink;
  try { sink = await openSink(filename); } catch (err) { if (err.name === "AbortError") return false; throw err; }
  try {
    let chunk = 8 * 1024 * 1024;
    let offset = 0;
    let total = Infinity;
    while (offset < total) {
      const t0 = performance.now();
      const { data, total: tot } = await fetchRange(url, offset, offset + chunk - 1, signal);
      total = tot || offset + data.length;
      await sink.write(data);
      offset += data.length;
      onProgress?.(Math.min(100, Math.round((offset / total) * 100)));
      const secs = (performance.now() - t0) / 1000;
      chunk = Math.max(MIN_CHUNK, Math.min(MAX_CHUNK, Math.round((data.length / Math.max(secs, 0.1)) * TARGET_SECONDS)));
      if (!data.length) break;
    }
    await sink.close();
    return true;
  } catch (err) {
    await sink.abort().catch(() => {});
    if (err.name === "AbortError") return false;
    throw err;
  }
}
